import { useEffect, useMemo, useRef, useState } from 'react';
import { PrinterOutlined } from '@ant-design/icons';
import { Button, Card, Empty, QRCode, Select, Spin, Typography } from 'antd';
import { useReactToPrint } from 'react-to-print';

import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { useScreen } from 'hooks/useScreen';

const { Text } = Typography;

const COMPANY_LOGO_URL =
  'https://oyu-dot-consulting.s3.eu-central-1.amazonaws.com/logo/shineunuudur-logo.png';
const QR_SIZE = 160;
// LOGO_SIZE must match the size-9 (36px) class on the overlay logo image.
const LOGO_SIZE = 36;
const ROWS = 2;
const COLS = 5;
// Keep in sync with the grid-cols-5 / grid-rows-2 classes on the page element.
const CARDS_PER_PAGE = ROWS * COLS;

interface ClassOption {
  id: number;
  name: string;
}

interface StudentMealQr {
  id: number;
  code: string;
  full_name: string;
  qr_value: string;
}

const MealQrPrinting = () => {
  const { runQuery } = useScreen();
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState<number>();
  const [students, setStudents] = useState<StudentMealQr[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    setClassesLoading(true);
    // Only the classes the user's Class scope covers.
    runQuery<ClassOption>('class_options')
      .then(setClasses)
      .finally(() => setClassesLoading(false));
  }, [runQuery]);

  useEffect(() => {
    if (!selectedClassId) {
      setStudents([]);
      return;
    }

    let ignore = false;
    setStudentsLoading(true);
    runQuery<StudentMealQr>('student_meal_qr', { classId: selectedClassId })
      .then(rows => {
        if (ignore) return;
        setStudents(rows);
        setSelectedIds([]);
      })
      .finally(() => {
        if (!ignore) setStudentsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [selectedClassId, runQuery]);

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'meal-qr',
    pageStyle: '@page { size: A4 landscape; margin: 0; }',
  });

  const sortedStudents = useMemo(
    () =>
      [...students].sort((a, b) =>
        a.full_name.localeCompare(b.full_name, 'mn'),
      ),
    [students],
  );

  const pages = useMemo(() => {
    const chosen =
      selectedIds.length === 0
        ? sortedStudents
        : sortedStudents.filter(student => selectedIds.includes(student.id));
    const result: StudentMealQr[][] = [];
    for (let i = 0; i < chosen.length; i += CARDS_PER_PAGE) {
      result.push(chosen.slice(i, i + CARDS_PER_PAGE));
    }
    return result;
  }, [sortedStudents, selectedIds]);

  const selectedClassName = classes.find(
    classItem => classItem.id === selectedClassId,
  )?.name;

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <div className="flex flex-wrap gap-3 mb-6">
          <Select
            placeholder="Анги сонгох"
            className="w-60"
            loading={classesLoading}
            value={selectedClassId}
            onChange={value => setSelectedClassId(value)}
            allowClear
            onClear={() => setSelectedClassId(undefined)}
            options={classes.map(classItem => ({
              value: classItem.id,
              label: classItem.name,
            }))}
          />

          <Select
            mode="multiple"
            allowClear
            showSearch={{ optionFilterProp: 'label' }}
            maxTagCount="responsive"
            placeholder="Бүх сурагч (сонгохгүй бол бүгд)"
            className="min-w-80 max-w-140"
            disabled={students.length === 0}
            value={selectedIds}
            onChange={value => setSelectedIds(value)}
            options={sortedStudents.map(student => ({
              value: student.id,
              label: student.full_name,
            }))}
          />

          <Button
            icon={<PrinterOutlined />}
            disabled={!selectedClassId || pages.length === 0}
            onClick={() => handlePrint()}
          >
            Хэвлэх
          </Button>
        </div>

        {studentsLoading && <Spin />}

        {!studentsLoading && selectedClassId && students.length === 0 && (
          <Empty description="Энэ ангид сурагч алга" />
        )}

        {!studentsLoading && students.length > 0 && (
          <div className="overflow-x-auto bg-[#f0f0f0] p-4 print:overflow-visible print:bg-transparent print:p-0">
            <div ref={printRef}>
              {pages.map((pageStudents, pageIndex) => (
                <div
                  key={pageIndex}
                  className="mx-auto mb-4 box-border grid h-[208mm] w-[297mm] grid-cols-5 grid-rows-2 gap-[2mm] overflow-hidden bg-white p-[8mm] shadow-[0_1px_6px_rgba(0,0,0,0.25)] not-last:print:break-after-page print:m-0 print:shadow-none"
                >
                  {pageStudents.map(student => (
                    <div key={student.id} className="min-h-0">
                      <div className="box-border h-full rounded border border-dashed border-[#999] p-1.5 text-center wrap-anywhere">
                        <Text type="secondary" className="block text-[9px]">
                          Шинэ Өнөөдөр {selectedClassName} анги
                        </Text>
                        <Text strong className="block mb-1 text-[10px]">
                          {student.full_name}
                        </Text>
                        <Text
                          type="secondary"
                          className="block mb-1 text-[8px]"
                        >
                          {student.code}
                        </Text>

                        <div className="flex justify-center relative">
                          <QRCode
                            type="svg"
                            value={student.qr_value}
                            size={QR_SIZE}
                            errorLevel="H"
                            color="#0b3d0b"
                            bgColor="#fff"
                            bordered={false}
                            icon={COMPANY_LOGO_URL}
                            iconSize={LOGO_SIZE}
                          />
                          <img
                            src={COMPANY_LOGO_URL}
                            alt=""
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-9 object-contain bg-white rounded-md"
                          />
                        </div>

                        <Text className="block mt-1 text-[9px]">
                          Хоолны бүртгэлийн QR
                        </Text>

                        <Text className="block mt-1 text-[8px] font-semibold text-[#cf1322]">
                          Энэ картыг зөвхөн эзэмшигч ашиглана, бусдад бүү
                          дамжуулна уу.
                        </Text>

                        <div className="mt-1 pt-1 text-[8px] text-[#595959] border-t border-dashed border-[#d9d9d9] text-left leading-normal">
                          Энэ QR кодыг цэцэрлэг/сургуулийн хоолны танхимд
                          уншуулж, сурагчийн хоолны бүртгэлийг хийнэ.
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </LayoutWithMenu>
  );
};

export default MealQrPrinting;
